import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { DatePicker } from "@/components/date-picker";
import { PhotoUpload } from "@/components/photo-upload";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BACKEND_URL } from "@/config";
import axios from "axios";
import { use } from "i18next";
import { useToken, useUser } from "@/global_state";
import { toast } from "react-toastify";
import { fetchUserInfo } from "@/utils";
import { useLocation } from "react-router-dom";
import { usePostHog } from "posthog-js/react";

interface Profile {
  name: string;
  bio: string | null;
  date_of_birth: string | null;
  image: string | null;
  image2: string | null;
  image3: string | null;
  image4: string | null;
  image5: string | null;
  image6: string | null;
  gender: string | null;
  genders_to_match: string[];
  min_matching_age: number;
  max_matching_age: number;
}

export default function ProfileEditPage() {
  const location = useLocation();
  const [user, setUser] = useUser();
  const posthog = usePostHog();
  const [authToken, setAuthToken] = useToken();
  const [profile, setProfile] = useState<Profile | null>(null);
  const photos: string[] = [
    profile?.image || "",
    profile?.image2 || "",
    profile?.image3 || "",
    profile?.image4 || "",
    profile?.image5 || "",
    profile?.image6 || "",
  ].filter(Boolean) as string[];
  const gender: string = profile?.gender || "male";
  const ageRange = [
    profile?.min_matching_age || 18,
    profile?.max_matching_age || 50,
  ];
  const dateOfBirth = profile?.date_of_birth
    ? new Date(profile?.date_of_birth)
    : undefined;
  const preferences = {
    male: profile?.genders_to_match?.includes("male") || false,
    female: profile?.genders_to_match?.includes("female") || false,
    non_binary: profile?.genders_to_match?.includes("non_binary") || false,
  };

  const fetchProfile = async () => {
    const response = await axios.get(`${BACKEND_URL}/api/users/me/profile/`);
    setProfile(response.data);
  };

  useEffect(() => {
    if (authToken) {
      fetchProfile();
    }
  }, [authToken]);

  const handlePhotoUpload = (newPhotos: string[]) => {
    if (profile) {
      const newProfile = { ...profile };
      newProfile.image = newPhotos[0];
      newProfile.image2 = newPhotos[1];
      newProfile.image3 = newPhotos[2];
      newProfile.image4 = newPhotos[3];
      newProfile.image5 = newPhotos[4];
      newProfile.image6 = newPhotos[5];
      setProfile(newProfile);
    }
  };

  const handlePreferenceChange = (pref: string, value: boolean) => {
    if (profile) {
      const newProfile = { ...profile };
      if (!newProfile.genders_to_match) {
        newProfile.genders_to_match = [];
      }
      if (value) {
        newProfile.genders_to_match.push(pref);
      } else {
        newProfile.genders_to_match = newProfile.genders_to_match.filter(
          (p) => p !== pref
        );
      }
      setProfile(newProfile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.image) {
      toast.error("Please upload a primary profile picture");
      return;
    }
    if (!profile.date_of_birth) {
      toast.error("Please select a date of birth");
      return;
    }
    const response = await axios.put(
      `${BACKEND_URL}/api/users/me/profile/`,
      JSON.stringify({
        ...profile,
      })
    );
    toast.success("Profile updated successfully!");
    fetchProfile();
    fetchUserInfo(location, posthog, setUser);
  };

  const handleCloseAccount = () => {
    // Handle account closure logic here
    console.log("Closing account");
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6 mb-16">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Edit Your Profile</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={profile?.name}
              onChange={(e) =>
                profile && setProfile({ ...profile, name: e.target.value })
              }
              className="bg-gray-800 border-gray-700 text-gray-100"
              required
            />
          </div>
          <div>
            <Label>Photos (Max 6)</Label>
            <PhotoUpload
              photos={photos}
              setPhotos={handlePhotoUpload}
              maxPhotos={6}
            />
          </div>
          <div>
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={profile?.bio || ""}
              onChange={(e) =>
                profile && setProfile({ ...profile, bio: e.target.value })
              }
              className="bg-gray-800 border-gray-700 text-gray-100"
              required
            />
          </div>
          <div>
            <Label>Date of Birth</Label>
            <DatePicker
              date={dateOfBirth}
              setDate={(date) =>
                profile &&
                setProfile({
                  ...profile,
                  date_of_birth: date?.toISOString().split("T")[0] || null,
                })
              }
            />
          </div>
          <div>
            <Label htmlFor="gender">Gender</Label>
            <select
              value={gender}
              onChange={(e) =>
                profile && setProfile({ ...profile, gender: e.target.value })
              }
              className="w-full bg-gray-800 border-gray-700 text-gray-100 p-2"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non_binary">Non-binary</option>
            </select>
          </div>
          <div>
            <Label>Matching Preferences</Label>
            <div className="space-y-4">
              <div className="flex space-x-4">
                {Object.entries(preferences).map(([key, value]) => (
                  <div key={key} className="flex items-center">
                    <Switch
                      id={key}
                      checked={value}
                      onCheckedChange={(val) =>
                        handlePreferenceChange(key, val)
                      }
                    />
                    <Label htmlFor={key} className="ml-2">
                      {key.charAt(0).toUpperCase() +
                        key.slice(1).replaceAll("_", " ")}
                    </Label>
                  </div>
                ))}
              </div>
              <div>
                <Label>
                  Age Range: {ageRange[0]} - {ageRange[1]}
                </Label>
                <RangeSlider
                  min={18}
                  max={100}
                  step={1}
                  value={ageRange}
                  onInput={(value: any) => {
                    profile &&
                      setProfile({
                        ...profile,
                        min_matching_age: value[0],
                        max_matching_age: value[1],
                      });
                  }}
                  className="mt-2"
                />
              </div>
            </div>
          </div>
          <Button
            type="submit"
            className="bg-[rgb(219,39,119)] hover:bg-[rgb(190,24,93)] text-white"
          >
            Save Changes
          </Button>
        </form>
        {/* <Card className="mt-12 border-red-500 bg-red-900/20 text-gray-50">
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">Close Account</h2>
            <div className="flex items-center space-x-2 text-red-400 mb-4">
              <AlertTriangle className="w-5 h-5" />
              <span>
                Warning: This action is irreversible and will permanently delete
                your account.
              </span>
            </div>
            <Button
              onClick={handleCloseAccount}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              Close Account
            </Button>
          </CardContent>
        </Card> */}
      </div>
    </div>
  );
}
